import { t } from './i18n.js';
import { useCallback, useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from 'lucide-react';
import { api, send } from './api.js';
import { IconButton, Loading, Modal, Notice } from './components.js';

type Field = 'from' | 'to' | 'subject' | 'body';
type Operator = 'contains' | 'is' | 'startsWith' | 'endsWith' | 'domainIs';
interface Condition {
  field: Field;
  operator: Operator;
  value: string;
}
interface Action {
  type: 'move' | 'markRead' | 'star' | 'stop';
  folderId?: string;
}
interface Rule {
  id: string;
  name: string;
  enabled: boolean;
  matchMode: 'all' | 'any';
  conditions: Condition[];
  actions: Action[];
}
interface Folder {
  id: string;
  name: string;
  kind: string;
}
const fields = (): [Field, string][] => [
  ['from', t('Expéditeur')],
  ['to', t('Destinataire')],
  ['subject', t('Objet')],
  ['body', t('Contenu')],
];
const operators = (): [Operator, string][] => [
  ['contains', 'contient'],
  ['is', t('est exactement')],
  ['startsWith', t('commence par')],
  ['endsWith', t('se termine par')],
  ['domainIs', t('a pour domaine')],
];
const blank = (): Rule => ({
  id: '',
  name: '',
  enabled: true,
  matchMode: 'all',
  conditions: [{ field: 'from', operator: 'contains', value: '' }],
  actions: [],
});
const summarize = (r: Rule) =>
  r.conditions.length === 1
    ? t('Si la condition correspond')
    : r.matchMode === 'all'
      ? t('Si les {n} conditions correspondent', { n: r.conditions.length })
      : t('Si l’une des {n} conditions correspond', { n: r.conditions.length });
const describe = (r: Rule, folders: Folder[]) => {
  const parts = r.actions.map((a) =>
    a.type === 'move'
      ? t('classer dans {folder}', {
          folder: folders.find((f) => f.id === a.folderId)?.name ?? t('un dossier retiré'),
        })
      : a.type === 'markRead'
        ? t('marquer comme lu')
        : a.type === 'star'
          ? t('mettre en favori')
          : t('arrêter les règles suivantes'),
  );
  return parts.join(', ');
};

export function MailRules({ notify, remote }: { notify: (s: string) => void; remote?: boolean }) {
  const [rules, setRules] = useState<Rule[]>([]),
    [folders, setFolders] = useState<Folder[]>([]),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(''),
    [draft, setDraft] = useState<Rule | undefined>(),
    [busy, setBusy] = useState(false),
    [confirm, setConfirm] = useState<Rule | undefined>();

  const load = useCallback(
    () =>
      api<{ rules: Rule[]; folders: Folder[] }>('/mail-rules')
        .then((r) => {
          setRules(r.rules);
          setFolders(r.folders);
          setError('');
        })
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false)),
    [],
  );
  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const body = {
        name: draft.name,
        enabled: draft.enabled,
        matchMode: draft.matchMode,
        conditions: draft.conditions,
        actions: draft.actions,
      };
      await send(
        '/mail-rules' + (draft.id ? '/' + draft.id : ''),
        body,
        draft.id ? 'PATCH' : 'POST',
      );
      setDraft(undefined);
      notify(draft.id ? t('Règle mise à jour.') : t('Règle créée.'));
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const move = async (index: number, delta: number) => {
    const next = [...rules];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setRules(next);
    try {
      await send('/mail-rules/order', { ids: next.map((r) => r.id) });
      await load();
    } catch (e: any) {
      setError(e.message);
      await load();
    }
  };
  const toggle = async (r: Rule) => {
    // Answer the click at once, then confirm with the server and reload on failure.
    const wanted = !r.enabled;
    setRules((current) => current.map((x) => (x.id === r.id ? { ...x, enabled: wanted } : x)));
    try {
      await send('/mail-rules/' + r.id, { ...r, enabled: wanted }, 'PATCH');
    } catch (e: any) {
      setError(e.message);
      await load();
    }
  };
  const remove = async (r: Rule) => {
    try {
      await api('/mail-rules/' + r.id, { method: 'DELETE' });
      setConfirm(undefined);
      notify(t('Règle supprimée.'));
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const setAction = (type: Action['type'], on: boolean) => {
    if (!draft) return;
    const others = draft.actions.filter((a) => a.type !== type);
    setDraft({
      ...draft,
      actions: on
        ? [...others, type === 'move' ? { type, folderId: folders[0]?.id } : { type }]
        : others,
    });
  };
  const moveAction = draft?.actions.find((a) => a.type === 'move');

  return (
    <div className="mail-rules">
      <div className="rules-head">
        <p className="rules-intro">
          {t(
            t(
              'Les règles s’appliquent aux messages reçus, dans l’ordre affiché, avant leur arrivée dans votre boîte. Elles ne transfèrent ni ne suppriment définitivement un message.',
            ),
          )}
        </p>
        <button className="primary" onClick={() => setDraft(blank())}>
          <Plus size={16} /> {t('Nouvelle règle')}
        </button>
      </div>
      {remote && (
        <Notice type="info">
          {t(t('Vos règles seront appliquées aux prochains messages après synchronisation.'))}
        </Notice>
      )}
      {error && <Notice type="error">{error}</Notice>}
      {loading ? (
        <Loading label={t('Chargement des règles…')} />
      ) : rules.length === 0 ? (
        <p className="rules-empty">
          {t('Aucune règle définie. Vos messages arrivent tous dans la boîte de réception.')}
        </p>
      ) : (
        <ol className="rules-list">
          {rules.map((r, i) => (
            <li key={r.id} className={r.enabled ? '' : 'disabled'}>
              <span className="rule-rank" aria-hidden="true">
                {i + 1}
              </span>
              <span className="rule-body">
                <strong>{r.name}</strong>
                <small>
                  {summarize(r)} : {describe(r, folders)}.
                </small>
              </span>
              <span className="rule-tools">
                <label className="rule-switch">
                  <input
                    type="checkbox"
                    checked={r.enabled}
                    onChange={() => void toggle(r)}
                    aria-label={t('Activer la règle {name}', { name: r.name })}
                  />
                  <span>{r.enabled ? t('Active') : t('Inactive')}</span>
                </label>
                <IconButton
                  label={t('Monter la règle {name}', { name: r.name })}
                  onClick={() => void move(i, -1)}
                  disabled={i === 0}
                >
                  <ArrowUp size={17} />
                </IconButton>
                <IconButton
                  label={t('Descendre la règle {name}', { name: r.name })}
                  onClick={() => void move(i, 1)}
                  disabled={i === rules.length - 1}
                >
                  <ArrowDown size={17} />
                </IconButton>
                <IconButton
                  label={t('Modifier la règle {name}', { name: r.name })}
                  onClick={() => setDraft(r)}
                >
                  <Pencil size={17} />
                </IconButton>
                <IconButton
                  label={t('Supprimer la règle {name}', { name: r.name })}
                  onClick={() => setConfirm(r)}
                >
                  <Trash2 size={17} />
                </IconButton>
              </span>
            </li>
          ))}
        </ol>
      )}
      {draft && (
        <Modal
          title={draft.id ? t('Modifier la règle') : t('Nouvelle règle')}
          onClose={() => setDraft(undefined)}
          wide
        >
          <form
            className="rule-form"
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            <label htmlFor="rule-name">{t('Nom de la règle')}</label>
            <input
              id="rule-name"
              required
              maxLength={120}
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
            <label htmlFor="rule-match">{t('Déclenchement')}</label>
            <select
              id="rule-match"
              value={draft.matchMode}
              onChange={(e) =>
                setDraft({ ...draft, matchMode: e.target.value as Rule['matchMode'] })
              }
            >
              <option value="all">{t('Toutes les conditions')}</option>
              <option value="any">{t('Au moins une condition')}</option>
            </select>
            <fieldset className="rule-conditions">
              <legend>{t('Conditions')}</legend>
              {draft.conditions.map((cond, i) => (
                <div className="rule-condition" key={i}>
                  <select
                    aria-label={t('Champ de la condition {n}', { n: i + 1 })}
                    value={cond.field}
                    onChange={(e) => {
                      const next = [...draft.conditions];
                      next[i] = { ...cond, field: e.target.value as Field };
                      setDraft({ ...draft, conditions: next });
                    }}
                  >
                    {fields().map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                  <select
                    aria-label={t('Opérateur de la condition {n}', { n: i + 1 })}
                    value={cond.operator}
                    onChange={(e) => {
                      const next = [...draft.conditions];
                      next[i] = { ...cond, operator: e.target.value as Operator };
                      setDraft({ ...draft, conditions: next });
                    }}
                  >
                    {operators().map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                  <input
                    aria-label={t('Valeur de la condition {n}', { n: i + 1 })}
                    required
                    maxLength={200}
                    value={cond.value}
                    onChange={(e) => {
                      const next = [...draft.conditions];
                      next[i] = { ...cond, value: e.target.value };
                      setDraft({ ...draft, conditions: next });
                    }}
                  />
                  {draft.conditions.length > 1 && (
                    <IconButton
                      label={t('Retirer la condition {n}', { n: i + 1 })}
                      onClick={() =>
                        setDraft({
                          ...draft,
                          conditions: draft.conditions.filter((_, n) => n !== i),
                        })
                      }
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  )}
                </div>
              ))}
              {draft.conditions.length < 10 && (
                <button
                  type="button"
                  className="text-button"
                  onClick={() =>
                    setDraft({
                      ...draft,
                      conditions: [
                        ...draft.conditions,
                        { field: 'from', operator: 'contains', value: '' },
                      ],
                    })
                  }
                >
                  {t('Ajouter une condition')}
                </button>
              )}
            </fieldset>
            <fieldset className="rule-actions">
              <legend>{t('Actions')}</legend>
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(moveAction)}
                  disabled={!folders.length}
                  onChange={(e) => setAction('move', e.target.checked)}
                />
                {t('Classer dans un dossier')}
              </label>
              {moveAction && (
                <select
                  aria-label={t('Dossier de destination')}
                  value={moveAction.folderId ?? ''}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      actions: draft.actions.map((a) =>
                        a.type === 'move' ? { ...a, folderId: e.target.value } : a,
                      ),
                    })
                  }
                >
                  {folders.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              )}
              <label>
                <input
                  type="checkbox"
                  checked={draft.actions.some((a) => a.type === 'markRead')}
                  onChange={(e) => setAction('markRead', e.target.checked)}
                />
                {t('Marquer comme lu')}
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={draft.actions.some((a) => a.type === 'star')}
                  onChange={(e) => setAction('star', e.target.checked)}
                />
                {t('Mettre en favori')}
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={draft.actions.some((a) => a.type === 'stop')}
                  onChange={(e) => setAction('stop', e.target.checked)}
                />
                {t('Ne pas appliquer les règles suivantes')}
              </label>
            </fieldset>
            <div className="modal-actions">
              <button type="button" className="text-button" onClick={() => setDraft(undefined)}>
                {t('Annuler')}
              </button>
              <button
                type="submit"
                className="primary"
                disabled={
                  busy || !draft.actions.some((a) => ['move', 'markRead', 'star'].includes(a.type))
                }
              >
                {busy ? t('Enregistrement…') : t('Enregistrer')}
              </button>
            </div>
          </form>
        </Modal>
      )}
      {confirm && (
        <Modal title={t('Supprimer cette règle')} onClose={() => setConfirm(undefined)}>
          <p className="modal-body">
            La règle {confirm.name} ne sera plus appliquée. Les messages déjà classés restent dans
            leur dossier.
          </p>
          <div className="modal-actions">
            <button className="text-button" onClick={() => setConfirm(undefined)}>
              {t('Annuler')}
            </button>
            <button className="danger" onClick={() => void remove(confirm)}>
              {t('Supprimer')}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
